import { Component, inject, OnInit, signal, ViewEncapsulation } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { EnpsComponent } from '@common/components/base/enps-component';
import { ObjectTypes } from '@common/enums/object-types';
import { GlobalTablesService } from '@common/services/global-tables-service';
import { LanguageSettings } from '@common/services/language-settings';
import { NavigationService } from '@common/services/navigation-service';
import { NomWebServicesJson } from '@common/services/nom-web-services-json';
import { StoryHttpActions } from '@common/store/story/actions/story.actions';
import { Store } from '@ngrx/store';
import { ViewStateService } from '@src/suite/common/services/view-state-service';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { PlaybookApiActions } from '@src/suite/common/store/playbook/playbook.actions';
import { RecordHttpActions } from '@src/suite/common/store/records/records.actions';
import { PlaybookService } from '@src/suite/playbook/services/playbook-service';
import { TopicService } from '@src/suite/playbook/services/topic-service';

import { LoaderComponent } from '../../../loader/loader';
import { TextFieldComponent } from '../../controls/text-field/text-field';

@Component({
    selector: 'st-create-new-template',
    imports: [LoaderComponent, MatButton, MatIcon, TextFieldComponent],
    templateUrl: './create-new-template.html',
    styleUrl: './create-new-template.scss',
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-create-new-template',
    },
})
export class CreateNewTemplateComponent extends EnpsComponent implements OnInit {
    navigationService = inject(NavigationService);
    nomWebServicesJson = inject(NomWebServicesJson);
    languageSettings = inject(LanguageSettings);
    topicService = inject(TopicService);
    dialogRef = inject<MatDialogRef<CreateNewTemplateComponent>>(MatDialogRef);
    playbookService = inject(PlaybookService);
    viewStateService = inject(ViewStateService);
    globalTablesService = inject(GlobalTablesService);
    data = inject<{
        record: StorytellingRecord;
        isFullscreen: boolean;
    }>(MAT_DIALOG_DATA);

    private store = inject(Store);

    i18n = this.store.selectSignal(selectI18N)();
    template: StorytellingRecord;
    readonly newTitleValue = signal<string>('');
    readonly loading = signal(false);

    get isDisabled(): boolean {
        return !this.data.record.Title || !this.data.record.Title.trim() || this.loading();
    }

    get isSuperVisor(): boolean {
        return this.nomWebServicesJson?.HelloResults?.Supervisor;
    }

    public ObjectTypes = ObjectTypes;
    ngOnInit(): void {
        this.template = this.data.record;
        this.newTitleValue.set(this.data.record.Title);
    }

    closeDialog() {
        this.dialogRef.close();
    }

    public async createTemplate(event: any, path: string) {
        this.loading.set(true);
        this.navigationService.closeCreateNewTemplate(event);
        // TODO: move to store
        if (this.data.record.Type === ObjectTypes.TOPIC) {
            await this.topicService.createTopicContent(this.data.record.Database, path, this.data.record.Guid, this.data.record.Path.groupId, false, null, null, null, this.newTitleValue());
            this.store.dispatch(PlaybookApiActions.loadTopicTemplates());
        } else if (this.data.record.Type === ObjectTypes.RUNDOWN) {
            this.store.dispatch(RecordHttpActions.saveRundownTemplate({ record: this.data.record as StorytellingRundownRecord }));
        } else {
            this.store.dispatch(StoryHttpActions.createStoryTemplate({ story: this.data.record as StorytellingStoryRecord, path, title: this.newTitleValue(), isFullscreen: this.data.isFullscreen }));
        }
        this.closeDialog();
    }
    public async createPersonalTemplate(event: any) {
        const destinationPath = `U_${this.nomWebServicesJson.UserId.toUpperCase()}\\L`;
        await this.createTemplate(event, destinationPath);
    }

    public async createTeamTemplate(event: any) {
        switch (this.data.record.Type) {
            case ObjectTypes.TOPIC:
                await this.createTemplate(event, `P_${this.navigationService.currentPlaybookTeam()}\\L`);
                break;
            case ObjectTypes.SCRIPT:
                await this.createTemplate(event, `P_${this.navigationService.currentPlaybookTeam()}\\X`);
                break;
            case ObjectTypes.RUNDOWN:
                await this.createTemplate(event, `P_${this.navigationService.currentPlaybookTeam()}\\L`);
                break;
        }
    }

    public async createEnterpriseTemplate(event: any) {
        switch (this.data.record.Type) {
            case ObjectTypes.TOPIC:
                await this.createTemplate(event, `P_${this.globalTablesService.EnterprisePlaybookGroup}\\L`);
                break;
            case ObjectTypes.SCRIPT:
                await this.createTemplate(event, `P_${this.globalTablesService.EnterprisePlaybookGroup}\\X`);
                break;
        }
    }

    get isRundown() {
        return this.data?.record.Type === ObjectTypes.RUNDOWN;
    }
}
